import React from 'react';
import './UserMenu.css';
import ProfileIcon from '../../assets/Icons/ProfileIcon.svg?react';
import GearIcon from '../../assets/Icons/GearIcon.svg?react';
import LogoutIcon from '../../assets/Icons/LogoutIcon.svg?react';
import { ControlOutlined, GiftOutlined, TeamOutlined, UploadOutlined } from '@ant-design/icons';

import { logout } from '../../containers/App/actions';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Suggestions, { Suggestion } from '../Suggestions/Suggestions';

import type { UserDto as User } from '@poker-stats/contracts';
import Avatar from '../Avatar/Avatar';
import useOutsideClick from '../../hooks/useOutsideClick';
import { useTranslation } from 'react-i18next';

type UserMenuProps = {
  user: User;
};
const UserMenu = ({ user }: UserMenuProps) => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getAbsoluteRef = React.useRef() as React.MutableRefObject<HTMLDivElement>;

  const [isHidden, setIsHidden] = React.useState(true);
  const handleToggleIsHidden = () => {
    setIsHidden((x) => !x);
  };

  const handleOutsideClick = React.useCallback(() => {
    setIsHidden(true);
  }, []);

  useOutsideClick(getAbsoluteRef, handleOutsideClick);

  const handlelogout = React.useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleRedirectUserProfile = React.useCallback(() => {
    navigate('/profile');
  }, [navigate]);

  const handleRedirectSettings = React.useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  const handleRedirectCooperation = React.useCallback(() => {
    navigate('/cooperation');
  }, [navigate]);

  const handleRedirectUsers = React.useCallback(() => {
    navigate('/users');
  }, [navigate]);

  const handleRedirectAdminReferrals = React.useCallback(() => {
    navigate('/admin/referrals');
  }, [navigate]);

  const handleRedirectAdmin = React.useCallback(() => {
    navigate('/admin');
  }, [navigate]);

  const handleRedirectMiningUpload = React.useCallback(() => {
    navigate('/admin/mining-upload');
  }, [navigate]);

  const userMenuItems = React.useMemo<Array<Suggestion>>(() => {
    const menuItems: Array<Suggestion> = [
      {
        id: 1,
        label: t('profile'),
        icon: <ProfileIcon />,
        onClick: handleRedirectUserProfile,
      },
      {
        id: 2,
        label: t('settings'),
        icon: <GearIcon />,
        onClick: handleRedirectSettings,
      },
      {
        id: 5,
        label: t('cooperation'),
        icon: <TeamOutlined />,
        onClick: handleRedirectCooperation,
      },
      {
        id: 3,
        label: t('logout'),
        icon: <LogoutIcon />,
        onClick: handlelogout,
      },
    ];
    if (user.isAdmin) {
      menuItems.push({
        id: 7,
        label: t('admin_title'),
        icon: <ControlOutlined />,
        onClick: handleRedirectAdmin,
      });
      menuItems.push({
        id: 8,
        label: t('admin_mining_upload'),
        icon: <UploadOutlined />,
        onClick: handleRedirectMiningUpload,
      });
      menuItems.push({
        id: 6,
        label: t('admin_referrals'),
        icon: <GiftOutlined />,
        onClick: handleRedirectAdminReferrals,
      });
      menuItems.push({
        id: 4,
        label: t('users'),
        icon: <ProfileIcon />,
        onClick: handleRedirectUsers,
      });
    }
    return menuItems;
  }, [
    handleRedirectUserProfile,
    handleRedirectSettings,
    handleRedirectCooperation,
    handlelogout,
    handleRedirectUsers,
    handleRedirectAdminReferrals,
    handleRedirectAdmin,
    handleRedirectMiningUpload,
    user,
    t,
  ]);

  return (
    <div className="UserMenu" ref={getAbsoluteRef} onClick={handleToggleIsHidden}>
      <Avatar user={user} />
      {!isHidden && <Suggestions className="UserMenu__Suggestions" suggestions={userMenuItems} />}
    </div>
  );
};

export default UserMenu;
